const STORAGE_KEY = 'bravara.blog.attribution.v1'
const MAX_PARAM_LENGTH = 2048
const MAX_URL_LENGTH = 4096

const AD_CLICK_PARAM_NAMES = [
  'fbclid',
  'fbc',
  '_fbc',
  'fbp',
  '_fbp',
  'gclid',
  'gbraid',
  'wbraid',
  'adct',
  'apple_adservices_token'
]

const UTM_PARAM_NAMES = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id'
]

const TRACKING_CONSENT_PARAM_NAMES = [
  'analytics_storage',
  'ad_storage',
  'ad_user_data',
  'ad_personalization',
  'marketing_consent',
  'gpc',
  'cookieyes_consent_id',
  'cookieyes_action',
  'tracking_consent_source',
  'tracking_consent_captured_at'
]

export const ATTRIBUTION_PARAM_NAMES = [...AD_CLICK_PARAM_NAMES, ...UTM_PARAM_NAMES]

const BRAVARA_ROOT_HOSTS = ['bravara.com', 'bravara.app']
const BLOG_HOSTS = ['blog.bravara.com', 'blog.bravara.app']

const isBrowser = () =>
  typeof window !== 'undefined' && typeof window.location !== 'undefined'

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key)

const cleanValue = (value, maxLength = MAX_PARAM_LENGTH) => {
  if (typeof value !== 'string') return null

  const cleaned = value.trim()

  if (!cleaned) return null

  return cleaned.slice(0, maxLength)
}

const normalizeConsentValue = value => {
  if (value === true) return 'granted'
  if (value === false) return 'denied'

  const normalized = cleanValue(String(value ?? ''))?.toLowerCase()
  if (!normalized) return null

  if (['yes', 'true', '1', 'granted', 'allow', 'allowed', 'accepted'].includes(normalized)) {
    return 'granted'
  }

  if (['no', 'false', '0', 'denied', 'deny', 'declined', 'rejected'].includes(normalized)) {
    return 'denied'
  }

  return null
}

const parseCookieYesConsentCookie = () => {
  if (!isBrowser() || typeof document === 'undefined') return {}

  const cookieValue = document.cookie
    .split('; ')
    .find(cookie => cookie.startsWith('cookieyes-consent='))
    ?.split('=')
    .slice(1)
    .join('=')

  if (!cookieValue) return {}

  return decodeURIComponent(cookieValue)
    .split(',')
    .reduce((values, pair) => {
      const [key, ...rawValue] = pair.split(':')
      const normalizedKey = cleanValue(key)?.toLowerCase()
      const value = cleanValue(rawValue.join(':'))

      if (normalizedKey && value) {
        values[normalizedKey] = value
      }

      return values
    }, {})
}

const readCookieYesApiConsent = () => {
  if (!isBrowser() || typeof window.getCkyConsent !== 'function') return {}

  try {
    const consent = window.getCkyConsent()

    return consent && typeof consent === 'object' ? consent : {}
  } catch (error) {
    return {}
  }
}

const readCategory = (apiConsent, cookieConsent, name) =>
  apiConsent?.categories?.[name] ??
  apiConsent?.[name] ??
  cookieConsent?.[name]

const getTrackingConsentParams = () => {
  if (!isBrowser()) return {}

  const cookieConsent = parseCookieYesConsentCookie()
  const apiConsent = readCookieYesApiConsent()
  const advertisementConsent = normalizeConsentValue(readCategory(apiConsent, cookieConsent, 'advertisement'))
  const analyticsConsent = normalizeConsentValue(readCategory(apiConsent, cookieConsent, 'analytics'))
  const actionValue =
    apiConsent?.isUserActionCompleted ??
    apiConsent?.action ??
    cookieConsent?.action
  const actionConsent = normalizeConsentValue(actionValue)
  const gpcEnabled = window.navigator?.globalPrivacyControl === true
  const consentId =
    cleanValue(apiConsent?.consentID) ||
    cleanValue(apiConsent?.consentId) ||
    cleanValue(cookieConsent?.consentid)

  const params = {
    analytics_storage: analyticsConsent,
    ad_storage: advertisementConsent,
    ad_user_data: advertisementConsent,
    ad_personalization: advertisementConsent,
    marketing_consent: advertisementConsent,
    gpc: gpcEnabled ? '1' : '0',
    cookieyes_consent_id: consentId,
    cookieyes_action: actionConsent,
    tracking_consent_source: advertisementConsent || analyticsConsent || actionConsent || gpcEnabled ? 'cookieyes' : null,
    tracking_consent_captured_at: new Date().toISOString()
  }

  return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== null && value !== undefined))
}

const hasAdvertisingConsent = (consentParams = getTrackingConsentParams()) => {
  if (consentParams.gpc === '1') return false

  return ['ad_storage', 'ad_user_data', 'ad_personalization', 'marketing_consent'].every(
    key => consentParams[key] === 'granted'
  )
}

const canPersistAttribution = (consentParams = getTrackingConsentParams()) =>
  hasAdvertisingConsent(consentParams) || consentParams.analytics_storage === 'granted'

const sanitizeAttributionUrl = (href, allowAdClickIds = false) => {
  const value = cleanValue(href, MAX_URL_LENGTH)
  if (!value || allowAdClickIds || !isBrowser()) return value

  try {
    const url = new URL(value, window.location.href)

    AD_CLICK_PARAM_NAMES.forEach(name => url.searchParams.delete(name))

    return url.toString()
  } catch (error) {
    return value
  }
}

const parseUrl = href => {
  if (!href || typeof href !== 'string' || !isBrowser()) return null

  try {
    return new URL(href, window.location.href)
  } catch (error) {
    return null
  }
}

const readStoredAttribution = () => {
  if (!isBrowser()) return {}

  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY)
    const parsed = stored ? JSON.parse(stored) : {}

    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (error) {
    return {}
  }
}

const writeStoredAttribution = attribution => {
  if (!isBrowser()) return

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution))
  } catch (error) {
    // Storage can be blocked in private browsing or hardened browsers.
  }
}

const filterAttributionForConsent = (attribution, allowAdClickIds) =>
  allowAdClickIds
    ? attribution
    : Object.fromEntries(
        Object.entries(attribution).filter(([key]) => !AD_CLICK_PARAM_NAMES.includes(key))
      )

const currentLandingUrl = (allowAdClickIds = false) => {
  if (!isBrowser()) return null

  return sanitizeAttributionUrl(
    `${window.location.origin}${window.location.pathname}${window.location.search}`,
    allowAdClickIds
  )
}

const externalReferrerUrl = (allowAdClickIds = false) => {
  if (
    !isBrowser() ||
    typeof document === 'undefined' ||
    !document.referrer
  ) {
    return null
  }

  try {
    const referrer = new URL(document.referrer)

    if (referrer.hostname === window.location.hostname) return null

    return sanitizeAttributionUrl(referrer.toString(), allowAdClickIds)
  } catch (error) {
    return sanitizeAttributionUrl(document.referrer, allowAdClickIds)
  }
}

const attributionFromCurrentUrl = () => {
  if (!isBrowser()) return {}

  const consentParams = getTrackingConsentParams()
  const allowAdClickIds = hasAdvertisingConsent(consentParams)
  const params = new URLSearchParams(window.location.search)
  const attributionParamNames = allowAdClickIds
    ? ATTRIBUTION_PARAM_NAMES
    : UTM_PARAM_NAMES
  const attribution = attributionParamNames.reduce((values, name) => {
    const value = cleanValue(params.get(name))

    if (value) values[name] = value

    return values
  }, {})

  const landingUrl = currentLandingUrl(allowAdClickIds)
  const referrerUrl = externalReferrerUrl(allowAdClickIds)

  if (landingUrl) attribution.landing_url = landingUrl
  if (referrerUrl) attribution.referrer_url = referrerUrl

  return attribution
}

const attributionForLinks = () => {
  if (!isBrowser()) return {}

  const consentParams = getTrackingConsentParams()
  const allowAdClickIds = hasAdvertisingConsent(consentParams)
  const storedAttribution = readStoredAttribution()

  return {
    ...filterAttributionForConsent(storedAttribution, allowAdClickIds),
    ...attributionFromCurrentUrl(),
    ...consentParams
  }
}

const isBravaraHost = hostname => {
  const host = hostname.toLowerCase()

  return BRAVARA_ROOT_HOSTS.some(
    rootHost => host === rootHost || host.endsWith(`.${rootHost}`)
  )
}

const isBlogHost = hostname => BLOG_HOSTS.includes(hostname.toLowerCase())

export const isAbsoluteHttpUrl = href => /^https?:\/\//i.test(href)

export const isBravaraAttributionUrl = href => {
  const url = parseUrl(href)

  if (!url || !['http:', 'https:'].includes(url.protocol)) return false

  return isBravaraHost(url.hostname) && !isBlogHost(url.hostname)
}

export const captureBravaraAttribution = () => {
  const consentParams = getTrackingConsentParams()
  const allowAdClickIds = hasAdvertisingConsent(consentParams)
  const storedAttribution = filterAttributionForConsent(
    readStoredAttribution(),
    allowAdClickIds
  )
  const currentAttribution = attributionFromCurrentUrl()

  if (!Object.keys(currentAttribution).length) {
    return {
      ...storedAttribution,
      ...consentParams
    }
  }

  const nextAttribution = {
    ...storedAttribution,
    ...currentAttribution
  }

  if (canPersistAttribution(consentParams)) {
    writeStoredAttribution(nextAttribution)
  }

  return {
    ...nextAttribution,
    ...consentParams
  }
}

export const withBravaraAttribution = (href, attribution = attributionForLinks()) => {
  if (!isBravaraAttributionUrl(href) || !Object.keys(attribution).length) {
    return href
  }

  const url = parseUrl(href)

  if (!url) return href

  ;[...ATTRIBUTION_PARAM_NAMES, ...TRACKING_CONSENT_PARAM_NAMES, 'landing_url', 'referrer_url'].forEach(name => {
    const value = attribution[name]

    if (value && !url.searchParams.has(name)) {
      url.searchParams.set(name, value)
    }
  })

  return url.toString()
}

/**
 * Attribution is added to Bravara links only at the moment a real click
 * navigates. The href in the rendered DOM (what crawlers and SSR HTML see) is
 * never rewritten, so bravara.com links stay clean and canonical. The
 * decorated URL is swapped in during the click event (the browser reads the
 * href after listeners run) and the original href is restored right after.
 */
const decorateLinkForNavigation = event => {
  const link =
    event.target && event.target.closest
      ? event.target.closest('a[href]')
      : null

  if (!link) return

  const href = link.getAttribute('href')
  const attributedHref = withBravaraAttribution(
    href,
    captureBravaraAttribution()
  )

  if (href === attributedHref) return

  link.setAttribute('href', attributedHref)

  window.setTimeout(() => {
    if (link.getAttribute('href') === attributedHref) {
      link.setAttribute('href', href)
    }
  }, 0)
}

export const attachBravaraAttributionClickHandler = () => {
  if (typeof document === 'undefined' || hasOwn(window, '__bravaraAttributionClickHandler')) {
    return
  }

  window.__bravaraAttributionClickHandler = true

  // `click` covers primary clicks, modifier clicks, and keyboard activation;
  // `auxclick` covers middle-click "open in new tab".
  document.addEventListener('click', decorateLinkForNavigation, true)
  document.addEventListener('auxclick', decorateLinkForNavigation, true)
}
